import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileText, Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import BoqAccommodationDrawingsPicker from '../../components/boq-bom/BoqAccommodationDrawingsPicker';
import {
  BoqAnalysisFooter,
  BoqAnalysisResults,
  BoqUploadFooter
} from '../../components/boq-bom/BoqAnalysisResults';
import { BoqGeneratedOutput } from '../../components/boq-bom/BoqGeneratedOutput';
import { CreateTasksFromBoqModal } from '../../components/boq-bom/CreateTasksFromBoqModal';
import {
  AccuracyRateGauge,
  BomSyncUnitModal,
  BoqBomCostingResults,
  BoqBomGeneratedFooter,
  BoqBomGeneratedResults,
  BOQ_BOM_GENERATED_SUBTITLE as BOM_GENERATED_SUBTITLE
} from '../../components/boq-bom/BoqBomGenerated';
import {
  BoqBomDetailChrome,
  BOQ_BOM_APP_TITLE,
  BOQ_BOM_DETAIL_CONTENT_OFFSET,
  BOQ_BOM_GENERATED_SUBTITLE
} from '../../components/boq-bom/BoqBomScreenHeader';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import Loader from '../../../shared/components/ui/Loader';
import {
  applyBomCosting,
  extractBoqData,
  generateBoq,
  generateBom,
  getAccommodationDrawings,
  getBoq,
  getBoqExtractDataStatus,
  getProjectCommonBoq,
  getProjectCommonBom,
  getSavedBom,
  getUnitsByProjectId,
  syncBomWithTask,
  updateBomMaterial,
  updateBoqRoomSpace,
  getAllInventoryByProjectId
} from '../../services/api/implementation/impl';
import {
  buildBomCostingSelections,
  getBomSyncCombinationName,
  buildBomMaterialPatch,
  buildInitialBomSelections,
  findUnitIdForAccommodationSync,
  mapGetBoqResponse,
  mapBoqExtractResponse,
  mapBoqGenerateResponse,
  mapBomApplyCostingResponse,
  mapBomGenerateResponse,
  isSavedBomReady,
  mapSavedBomResponse,
  mergeMaterialUpdateIntoCostingData,
  parseBomNumber,
  getApiErrorMessage,
  isBoqRegenerationRequiredForBomError,
  isGroupCommonExtractRequiredError,
  mapRoomSpaceRows
} from '../../utils/boqBomExtract';
import {
  buildApplyBomCostingPayload,
  buildBoqExtractDataOptions,
  buildBoqIntelligenceOptions,
  buildGenerateBomOptions,
  buildGenerateBoqOptions,
  buildGetBoqOptions,
  getProjectCommonCombinationCount,
  buildSyncBomPayload,
  getBoqBomAccommodationId,
  getBoqBomBreadcrumbFlowLabel,
  getBoqBomDisplayName,
  getBoqBomQuestionsPath,
  getBoqBomWorkTypeIds,
  getBomSyncUnitType,
  resolveBoqBomFlow,
  resolveBoqBomFlowApiOptions
} from '../../utils/boqBomRoutes';
import { normalizeDrawings } from '../../utils/accommodationDrawingUtils';
import { pollBoqExtractJob } from '../../utils/boqExtractPolling';

const BOQ_BOM_PROJECT_STORAGE_KEY = 'boqBomSelectedProjectId';

const DETAIL_TABS = ['BOQ', 'BOM'];
// Kept in step with src/modules/boq/drawingFormats.ts on the backend, which
// refuses anything outside this list at upload time.
const FILE_FORMATS = ['PDF', 'DXF', 'PNG / JPG', 'XLSX', 'CSV'];
const ACCEPTED_FILE_TYPES = [
  '.pdf,application/pdf',
  '.dxf,image/vnd.dxf',
  '.png,.jpg,.jpeg,.webp,.bmp,image/png,image/jpeg,image/webp,image/bmp',
  '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel',
  '.csv,.txt,text/csv,text/plain'
].join(',');

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const BoqFileUploadSection = ({
  categoryName,
  files,
  fileInputRef,
  isDragging,
  onDragOver,
  onDragLeave,
  onDrop,
  onBrowseFiles,
  onFileInputChange,
  onRemoveFile,
  optionalUpload = false
}) => (
  <div className={optionalUpload ? '' : 'mx-auto max-w-[720px]'}>
    {!optionalUpload ? (
      <div className="text-center mb-2">
        <h2 className="text-[18px] font-semibold text-[#111827] leading-snug">
          Upload all the files you have for {categoryName}
        </h2>
        <p className="mt-3 text-[15px] text-[#4B5563] leading-relaxed max-w-[560px] mx-auto">
          It doesn&apos;t matter how many or what format. Upload everything — AI will read them all
          and tell you what&apos;s still missing.
        </p>
      </div>
    ) : (
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-[#111827]">Additional files</h3>
        <p className="mt-0.5 text-xs text-[#6B7280]">
          Optional extra PDFs for this analysis only (not saved to accommodation).
        </p>
      </div>
    )}

    <input
      ref={fileInputRef}
      type="file"
      multiple
      accept={ACCEPTED_FILE_TYPES}
      className="sr-only"
      onChange={onFileInputChange}
      aria-label={`Upload files for ${categoryName}`}
    />

    <div
      role="button"
      tabIndex={0}
      onClick={onBrowseFiles}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onBrowseFiles();
        }
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`cursor-pointer rounded-xl border-2 border-dashed text-center transition-colors ${
        optionalUpload ? 'bg-[#FAFBFC] px-4 py-5' : 'bg-white px-6 pt-7 pb-8'
      } ${
        isDragging ? 'border-[#2A6A73] bg-[#F4F9FA]' : 'border-[#C5CCD3] hover:border-[#9AB4BA] hover:bg-[#FAFBFC]'
      }`}>
      <div
        className={`mx-auto flex items-center justify-center rounded-lg bg-[#ECEEF1] text-[#6B7280] ${
          optionalUpload ? 'h-10 w-10' : 'h-12 w-12'
        }`}>
        <Upload size={optionalUpload ? 18 : 22} strokeWidth={1.75} />
      </div>
      <p
        className={`font-semibold text-[#111827] ${optionalUpload ? 'mt-2 text-sm' : 'mt-4 text-[17px]'}`}>
        {optionalUpload ? 'Drop extra PDFs here' : 'Drop all your files here'}
      </p>
      <p
        className={`font-medium text-[#2A6A73] ${optionalUpload ? 'mt-1 text-xs' : 'mt-1.5 text-[14px]'}`}>
        or click to browse
      </p>
      {!optionalUpload ? (
        <>
          <p className="mt-2.5 text-[14px] text-[#4B5563] leading-relaxed">
            Plans · Section · Structural drawings · Column details · Elevations · Specifications
          </p>
          <p className="mt-2 text-[13px] text-[#6B7280] leading-relaxed">
            Upload everything you have — even partial or low-quality scans
          </p>
        </>
      ) : (
        <p className="mt-1.5 text-xs text-[#6B7280]">
          PDF, DXF, images, XLSX or CSV · max 50 MB per file
        </p>
      )}
      {!optionalUpload ? (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {FILE_FORMATS.map((type) => (
            <span
              key={type}
              className="rounded-full border border-[#D1D5DB] bg-white px-3 py-1 text-[12px] font-medium text-[#4B5563]">
              {type}
            </span>
          ))}
        </div>
      ) : null}
    </div>

    {files.length > 0 ? (
      <div className={`space-y-2 ${optionalUpload ? 'mt-3' : 'mt-5'}`}>
        {files.map((file) => (
          <div
            key={file.id}
            className="flex items-center justify-between rounded-lg border border-[#E5E7EB] bg-white px-3 py-2.5">
            <div className="flex min-w-0 items-center gap-2.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#DEF4EC] text-[#20A273]">
                <FileText size={16} strokeWidth={1.75} />
              </div>
              <div className="min-w-0 text-left">
                <p className="truncate text-sm font-medium text-[#111827]">{file.name}</p>
                <p className="text-xs text-[#6B7280]">{file.size}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onRemoveFile(file.id)}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#D1D5DB] text-[#9CA3AF] transition-colors hover:border-[#9CA3AF] hover:text-[#374151]"
              aria-label={`Remove ${file.name}`}>
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    ) : null}
  </div>
);

const BomTabPanel = ({ categoryName }) => (
  <div className="mx-auto max-w-[720px]">
    <div className="rounded-xl border border-[#E5E7EB] bg-white px-6 py-12 text-center shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
      <p className="text-[15px] font-medium text-[#111827]">BOM for {categoryName}</p>
      <p className="mt-2 text-[14px] text-[#6B7280]">
        Bill of Materials will be available after BOQ analysis is complete.
      </p>
    </div>
  </div>
);

const BoqBomDetail = () => {
  const { collapsed } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { projects } = useProjectsList();
  const [activeTab, setActiveTab] = useState('BOQ');
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [bomGenerated, setBomGenerated] = useState(false);
  const [boqGenerated, setBoqGenerated] = useState(false);
  const [generatedBoqData, setGeneratedBoqData] = useState(null);
  const [generatedBomData, setGeneratedBomData] = useState(null);
  const [costingData, setCostingData] = useState(null);
  const [bomCostingApplied, setBomCostingApplied] = useState(false);
  const [savedBomReady, setSavedBomReady] = useState(false);
  const [bomSelections, setBomSelections] = useState({});
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isExportingBom, setIsExportingBom] = useState(false);
  const [isApplyingCosting, setIsApplyingCosting] = useState(false);
  const [isSyncingWithTask, setIsSyncingWithTask] = useState(false);
  const [isSyncUnitModalOpen, setIsSyncUnitModalOpen] = useState(false);
  const [isCreateTasksModalOpen, setIsCreateTasksModalOpen] = useState(false);
  const [savingMaterialRowId, setSavingMaterialRowId] = useState(null);
  const [isGeneratingBoq, setIsGeneratingBoq] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [isRestoringBoq, setIsRestoringBoq] = useState(false);
  const [needsBoqRegeneration, setNeedsBoqRegeneration] = useState(false);
  const [extractRequiredError, setExtractRequiredError] = useState(null);
  const [shouldOpenFilePicker, setShouldOpenFilePicker] = useState(false);
  const [bomAlreadyExists, setBomAlreadyExists] = useState(
    () => Boolean(location.state?.hasBom)
  );
  const [useAllDrawings, setUseAllDrawings] = useState(false);
  const [selectedDrawingFileKeys, setSelectedDrawingFileKeys] = useState([]);
  const [analyzePhase, setAnalyzePhase] = useState(null);
  const extractAbortRef = useRef(null);

  const readyForBoqEngine = Boolean(analysisData?.readyForBoqEngine);

  const decodedName = useMemo(
    () => getBoqBomDisplayName(params, location.state),
    [params, location.state]
  );

  const breadcrumbFlowLabel = useMemo(
    () => getBoqBomBreadcrumbFlowLabel(location.pathname),
    [location.pathname]
  );

  const flowApiOptions = useMemo(
    () => resolveBoqBomFlowApiOptions(location.pathname, location.state),
    [location.pathname, location.state]
  );

  const boqBomFlow = resolveBoqBomFlow(location.pathname);
  const isCommonAreaFlow = boqBomFlow === 'common-area';
  /** Independent/group unit BOQs can create live tasks; block/common-area BOQs cannot yet. */
  const boqTaskKind = isCommonAreaFlow ? null : getBomSyncUnitType(location.pathname);

  const accommodationId = useMemo(
    () => getBoqBomAccommodationId(params, location.state),
    [params, location.state]
  );

  const isAccommodationDrawingFlow = !isCommonAreaFlow && Boolean(accommodationId);

  const {
    data: accommodationDrawingsRaw,
    isLoading: isLoadingAccommodationDrawings,
    isError: isAccommodationDrawingsError
  } = useQuery({
    queryKey: ['accommodationDrawings', accommodationId],
    queryFn: () => getAccommodationDrawings(accommodationId),
    enabled: isAccommodationDrawingFlow
  });

  const accommodationDrawings = useMemo(
    () => normalizeDrawings(accommodationDrawingsRaw),
    [accommodationDrawingsRaw]
  );

  const projectId = useMemo(() => {
    if (location.state?.projectId != null) return Number(location.state.projectId);
    const savedId = localStorage.getItem(BOQ_BOM_PROJECT_STORAGE_KEY);
    return savedId ? parseInt(savedId, 10) : null;
  }, [location.state]);

  const projectName = useMemo(() => {
    if (location.state?.projectName) return location.state.projectName;
    if (!projectId || !projects?.length) return 'Project';
    return projects.find((p) => p.id === projectId)?.name ?? 'Project';
  }, [location.state, projectId, projects]);

  const workTypeIds = useMemo(() => getBoqBomWorkTypeIds(location.state), [location.state]);

  const handleRemoveFile = (id) => {
    setFiles((prev) => prev.filter((file) => file.id !== id));
  };

  const addFiles = (fileList) => {
    if (!fileList?.length) return;

    const incoming = Array.from(fileList).map((file, index) => ({
      id: `${file.name}-${file.size}-${file.lastModified}-${Date.now()}-${index}`,
      name: file.name,
      size: formatFileSize(file.size),
      file
    }));

    setFiles((prev) => [...prev, ...incoming]);
  };

  const handleBrowseFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    addFiles(e.target.files);
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const handleToggleDrawingFileKey = (fileKey) => {
    setSelectedDrawingFileKeys((prev) =>
      prev.includes(fileKey) ? prev.filter((key) => key !== fileKey) : [...prev, fileKey]
    );
  };

  const handleUseAllDrawingsChange = (checked) => {
    setUseAllDrawings(checked);
    if (checked) {
      setSelectedDrawingFileKeys([]);
    }
  };

  const handleSelectAllDrawings = () => {
    const keys = accommodationDrawings
      .map((drawing) => drawing.fileKey)
      .filter(Boolean);
    setSelectedDrawingFileKeys(keys);
    setUseAllDrawings(false);
  };

  const handleClearDrawingSelection = () => {
    setSelectedDrawingFileKeys([]);
  };

  const handleAnalyzeFiles = async () => {
    if (isCommonAreaFlow) {
      if (!projectId) {
        toast.error('Project is required to analyze common area files.');
        return;
      }
      if (!workTypeIds.length) {
        toast.error('Common area work types are required to analyze files.');
        return;
      }
    } else if (!accommodationId) {
      toast.error('Accommodation is required to analyze files. Open this page from a group or independent accommodation.');
      return;
    }

    const rawFiles = files.map((entry) => entry.file).filter(Boolean);
    const hasFiles = rawFiles.length > 0;
    const hasUseAll = isAccommodationDrawingFlow && useAllDrawings;
    const hasSelectedKeys =
      isAccommodationDrawingFlow && !useAllDrawings && selectedDrawingFileKeys.length > 0;

    if (isCommonAreaFlow) {
      if (!hasFiles) {
        toast.error('Please upload at least one file.');
        return;
      }
    } else if (!hasUseAll && !hasSelectedKeys && !hasFiles) {
      toast.error('Select accommodation drawings, use all drawings, or upload at least one file.');
      return;
    }

    extractAbortRef.current?.abort();
    const abortController = new AbortController();
    extractAbortRef.current = abortController;

    setIsAnalyzing(true);
    setAnalyzePhase('uploading');
    try {
      const extractOptions = isCommonAreaFlow
        ? buildBoqExtractDataOptions(flowApiOptions, {
            projectId,
            workTypeIds,
            workTypes: location.state?.workTypes
          })
        : buildBoqIntelligenceOptions(flowApiOptions);

      if (isAccommodationDrawingFlow) {
        if (useAllDrawings) {
          extractOptions.useAllDrawings = true;
        } else if (selectedDrawingFileKeys.length > 0) {
          extractOptions.useAllDrawings = false;
          extractOptions.drawingFileKeys = selectedDrawingFileKeys;
        }
      }

      const postResponse = await extractBoqData(
        isCommonAreaFlow ? null : accommodationId,
        rawFiles,
        extractOptions
      );

      const jobId = postResponse?.data?.jobId ?? postResponse?.jobId;
      if (!jobId) {
        throw new Error('Extraction job was not created. Please try again.');
      }

      setAnalyzePhase('analyzing');
      const result = await pollBoqExtractJob(jobId, getBoqExtractDataStatus, {
        signal: abortController.signal
      });

      setAnalysisData(mapBoqExtractResponse(result));
      setAnalysisComplete(true);
      toast.success('Files analyzed successfully');
    } catch (err) {
      if (err?.name !== 'AbortError' && err?.message !== 'Drawing extraction cancelled') {
        toast.error(err?.response?.data?.message || err?.message || 'Failed to analyze files');
      }
    } finally {
      setIsAnalyzing(false);
      setAnalyzePhase(null);
      extractAbortRef.current = null;
    }
  };

  const handleUploadMore = () => {
    setShouldOpenFilePicker(true);
    setAnalysisComplete(false);
    setBomGenerated(false);
    setBoqGenerated(false);
    setGeneratedBoqData(null);
    setGeneratedBomData(null);
    setCostingData(null);
    setBomCostingApplied(false);
    setSavedBomReady(false);
    setBomSelections({});
    setAnalysisData(null);
    setExtractRequiredError(null);
    setUseAllDrawings(false);
    setSelectedDrawingFileKeys([]);
    setAnalyzePhase(null);
  };

  useEffect(() => {
    if (!analysisComplete && shouldOpenFilePicker && fileInputRef.current) {
      fileInputRef.current.click();
      setShouldOpenFilePicker(false);
    }
  }, [analysisComplete, shouldOpenFilePicker]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(
    () => () => {
      extractAbortRef.current?.abort();
    },
    []
  );

  const canAnalyzeDrawings = useMemo(() => {
    if (isCommonAreaFlow) {
      return Boolean(projectId && workTypeIds.length && files.length > 0);
    }
    if (!accommodationId) return false;
    return useAllDrawings || selectedDrawingFileKeys.length > 0 || files.length > 0;
  }, [
    accommodationId,
    files.length,
    isCommonAreaFlow,
    projectId,
    selectedDrawingFileKeys.length,
    useAllDrawings,
    workTypeIds.length
  ]);

  const analyzeLoadingLabel =
    analyzePhase === 'uploading' ? 'Uploading…' : 'Analyzing drawings…';

  const applyRestoredBoqState = (restored, savedBomResponse) => {
    const restoredBoqId =
      restored.boqId ?? restored.generatedBoqData?.boqId ?? restored.generatedBomData?.boqId ?? null;

    if (restored.analysisData) {
      setAnalysisData(restored.analysisData);
    }
    if (restored.analysisComplete || restored.boqGenerated || restored.bomGenerated) {
      setAnalysisComplete(true);
    }
    if (restored.generatedBoqData) {
      setGeneratedBoqData(restored.generatedBoqData);
      setBoqGenerated(true);
    }
    if (restored.generatedBomData) {
      setGeneratedBomData(restored.generatedBomData);
      setBomGenerated(true);
      setBomAlreadyExists(true);
      if (restored.generatedBomData.workSections?.length) {
        setBomSelections(buildInitialBomSelections(restored.generatedBomData.workSections));
      }
    }
    if (restored.bomCostingApplied && restored.costingData) {
      setCostingData(restored.costingData);
      setBomCostingApplied(true);
      setBomGenerated(true);
      setBomAlreadyExists(true);
    }

    if (isSavedBomReady(savedBomResponse, restoredBoqId)) {
      setCostingData(mapSavedBomResponse(savedBomResponse, null, restoredBoqId));
      setBomCostingApplied(true);
      setBomGenerated(true);
      setSavedBomReady(true);
      setBomAlreadyExists(true);
      setActiveTab('BOM');
    } else {
      setSavedBomReady(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const restoreSavedBoq = async () => {
      setIsRestoringBoq(true);
      try {
        if (isCommonAreaFlow) {
          if (!projectId) return;

          const boqResponse = await getProjectCommonBoq(projectId);
          if (cancelled) return;

          const restored = boqResponse ? mapGetBoqResponse(boqResponse) : {};
          const savedBomResponse = await getProjectCommonBom(projectId);
          if (cancelled) return;

          applyRestoredBoqState(restored, savedBomResponse);
          return;
        }

        if (!accommodationId) return;

        const boqResponse = await getBoq(accommodationId, buildGetBoqOptions(flowApiOptions));
        if (cancelled) return;

        const restored = boqResponse ? mapGetBoqResponse(boqResponse) : {};
        const savedBomResponse = await getSavedBom(accommodationId);
        if (cancelled) return;

        applyRestoredBoqState(restored, savedBomResponse);
      } catch (err) {
        if (!cancelled) {
          toast.error(err?.response?.data?.message || err?.message || 'Failed to load saved BOQ');
        }
      } finally {
        if (!cancelled) setIsRestoringBoq(false);
      }
    };

    restoreSavedBoq();

    return () => {
      cancelled = true;
    };
  }, [accommodationId, isCommonAreaFlow, projectId]);

  useEffect(() => {
    if (location.state?.returnTab) {
      setActiveTab(location.state.returnTab);
    }
  }, [location.state?.returnTab]);

  useEffect(() => {
    if (location.state?.startUpload) {
      handleUploadMore();
      return;
    }

    const hasNavigationState =
      location.state?.analysisComplete ||
      location.state?.analysisData ||
      location.state?.boqGenerated ||
      location.state?.bomGenerated ||
      location.state?.generatedBoqData ||
      location.state?.generatedBomData ||
      location.state?.costingData ||
      location.state?.bomCostingApplied ||
      location.state?.bomSelections;

    if (!hasNavigationState) return;

    if (location.state?.analysisData) {
      setAnalysisData(location.state.analysisData);
    }
    if (location.state?.analysisComplete) {
      setAnalysisComplete(true);
    }
    if (location.state?.bomGenerated != null) {
      setBomGenerated(Boolean(location.state.bomGenerated));
    }
    if (location.state?.boqGenerated != null) {
      setBoqGenerated(Boolean(location.state.boqGenerated));
    }
    if (location.state?.generatedBoqData != null) {
      setGeneratedBoqData(location.state.generatedBoqData);
    }
    if (location.state?.generatedBomData != null) {
      setGeneratedBomData(location.state.generatedBomData);
    }
    if (location.state?.costingData != null) {
      setCostingData(location.state.costingData);
    }
    if (location.state?.bomCostingApplied != null) {
      setBomCostingApplied(Boolean(location.state.bomCostingApplied));
    }
    if (
      location.state?.bomCostingApplied &&
      (location.state?.costingData?.savedMaterials?.length ?? 0) > 0
    ) {
      setSavedBomReady(true);
    }
    if (location.state?.bomSelections) {
      setBomSelections(location.state.bomSelections);
    }
  }, [
    location.pathname,
    decodedName,
    location.state?.analysisComplete,
    location.state?.analysisData,
    location.state?.returnTab,
    location.state?.bomGenerated,
    location.state?.boqGenerated,
    location.state?.generatedBoqData,
    location.state?.generatedBomData,
    location.state?.costingData,
    location.state?.bomCostingApplied,
    location.state?.bomSelections,
    location.state?.startUpload
  ]);

  useEffect(() => {
    if (generatedBomData?.workSections?.length && !bomCostingApplied) {
      setBomSelections((prev) => {
        if (Object.keys(prev).length > 0) return prev;
        return buildInitialBomSelections(generatedBomData.workSections);
      });
    }
  }, [generatedBomData?.workSections, bomCostingApplied]);

  const navigateToQuestions = (topicLabel) => {
    navigate(getBoqBomQuestionsPath(location.pathname, decodedName, params), {
      state: {
        ...location.state,
        categoryName: decodedName,
        projectName,
        projectId,
        topicLabel,
        documentType: topicLabel,
        analysisData,
        analysisComplete: true,
        accommodationId,
        flowApiOptions,
        workTypes: location.state?.workTypes
      }
    });
  };

  const handleSaveRoom = async (row, patch) => {
    try {
      const response = await updateBoqRoomSpace(row.roomId, patch);
      const [updated] = mapRoomSpaceRows([response?.data ?? {}]);
      setAnalysisData((prev) =>
        prev
          ? {
              ...prev,
              roomSpaceRows: (prev.roomSpaceRows ?? []).map((r) =>
                r.id === row.id ? { ...updated, id: r.id } : r
              )
            }
          : prev
      );
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save room dimensions'));
      throw err;
    }
  };

  const handleAnswerQuestions = (item) => {
    if (!analysisComplete || !analysisData) {
      toast.error('Analyze files first.');
      return;
    }
    navigateToQuestions(item?.label ?? 'Clarification questions');
  };

  const runGenerateBoq = async ({ skipReadyCheck = false, successMessage } = {}) => {
    if (!skipReadyCheck && !readyForBoqEngine) {
      toast.error(
        analysisData?.footerNote ??
          'Complete clarification questions before generating BOQ.'
      );
      return false;
    }
    if (isCommonAreaFlow) {
      if (!projectId) {
        toast.error('Project is required to generate BOQ.');
        return false;
      }
      if (!workTypeIds.length) {
        toast.error('Common area work types are required to generate BOQ.');
        return false;
      }
    } else if (!accommodationId) {
      toast.error('Accommodation is required to generate BOQ.');
      return false;
    }

    setIsGeneratingBoq(true);
    try {
      const generateOptions = isCommonAreaFlow
        ? buildGenerateBoqOptions(flowApiOptions, {
            projectId,
            workTypeIds,
            workTypes: location.state?.workTypes
          })
        : buildGenerateBoqOptions(flowApiOptions);

      const response = await generateBoq(
        isCommonAreaFlow ? null : accommodationId,
        generateOptions
      );
      const mapped = mapBoqGenerateResponse(response);
      setGeneratedBoqData(mapped);
      setBoqGenerated(true);
      setNeedsBoqRegeneration(false);
      setExtractRequiredError(null);
      window.scrollTo(0, 0);
      toast.success(successMessage ?? 'BOQ generated successfully');
      return true;
    } catch (err) {
      const message = getApiErrorMessage(err, 'Failed to generate BOQ');
      if (isGroupCommonExtractRequiredError(err)) {
        setExtractRequiredError(message);
      }
      toast.error(message);
      return false;
    } finally {
      setIsGeneratingBoq(false);
    }
  };

  const handleGenerateBoq = () => runGenerateBoq();

  const handleReupload = () => {
    handleUploadMore();
    setNeedsBoqRegeneration(false);
    setFiles([]);
    window.scrollTo(0, 0);
  };

  const handleRegenerateBoq = () => {
    handleReupload();
  };

  const handleExportBom = async () => {
    if (isCommonAreaFlow) {
      if (!projectId) {
        toast.error('Project is required to generate BOM.');
        return;
      }
    } else if (!accommodationId) {
      toast.error('Accommodation is required to generate BOM.');
      return;
    }

    const boqId = generatedBoqData?.boqId;
    if (!boqId) {
      toast.error('Generate BOQ first before exporting BOM.');
      return;
    }

    const combinationCount = isCommonAreaFlow
      ? getProjectCommonCombinationCount(workTypeIds)
      : 2;

    setIsExportingBom(true);
    try {
      const response = await generateBom(
        isCommonAreaFlow ? null : accommodationId,
        boqId,
        combinationCount,
        buildGenerateBomOptions(
          flowApiOptions,
          isCommonAreaFlow ? { projectId } : {}
        )
      );
      const mapped = mapBomGenerateResponse(response);
      setGeneratedBomData(mapped);
      setBomSelections(buildInitialBomSelections(mapped.workSections));
      setCostingData(null);
      setBomCostingApplied(false);
      setBomGenerated(true);
      setNeedsBoqRegeneration(false);
      setActiveTab('BOM');
      window.scrollTo(0, 0);
      toast.success('BOM generated successfully');
    } catch (err) {
      const message = getApiErrorMessage(err, 'Failed to generate BOM');
      toast.error(message);
      if (isBoqRegenerationRequiredForBomError(err)) {
        setNeedsBoqRegeneration(true);
      }
    } finally {
      setIsExportingBom(false);
    }
  };

  // Project inventory for linking saved BOM materials (needed before BOM → task sync).
  const { data: inventoryOptions } = useQuery({
    queryKey: ['boqBomInventoryOptions', projectId],
    queryFn: async () => {
      // All items, so a BOM already linked to a switched-off item still
      // shows its link; inactive ones are tagged in the dropdown.
      const res = await getAllInventoryByProjectId(projectId, null, '', '', 'all');
      const rows = Array.isArray(res) ? res : res?.data ?? res?.items ?? [];
      return rows
        .filter((item) => item?.id != null)
        .map((item) => ({
          id: item.id,
          name: item.item_name || `Item ${item.id}`,
          unit: item.unit,
          isActive: item.is_active !== false
        }));
    },
    enabled: Boolean(projectId) && activeTab === 'BOM' && bomCostingApplied
  });
  const [linkingRowId, setLinkingRowId] = useState(null);

  const handleLinkBomInventory = async (material, inventoryId) => {
    setLinkingRowId(material.rowId);
    try {
      await updateBomMaterial(material.rowId, { inventory_id: inventoryId });
      setCostingData((prev) =>
        mergeMaterialUpdateIntoCostingData(prev, material.rowId, { inventoryId })
      );
      toast.success(inventoryId ? 'Material linked to inventory' : 'Inventory link removed');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to link inventory item'));
    } finally {
      setLinkingRowId(null);
    }
  };

  const handleSaveBomMaterial = async (rowId, original, edited) => {
    const normalized = {
      ...edited,
      quantity: parseBomNumber(edited.quantity),
      unitRate: parseBomNumber(edited.unitRate),
      amount: parseBomNumber(edited.amount),
      inventoryId:
        edited.inventoryId != null ? parseBomNumber(edited.inventoryId) : edited.inventoryId
    };

    const patch = buildBomMaterialPatch(original, normalized);
    if (Object.keys(patch).length === 0) {
      return true;
    }

    setSavingMaterialRowId(rowId);
    try {
      await updateBomMaterial(rowId, patch);
      setCostingData((prev) =>
        mergeMaterialUpdateIntoCostingData(prev, rowId, normalized)
      );
      toast.success('Material updated');
      return true;
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to update material'
      );
      return false;
    } finally {
      setSavingMaterialRowId(null);
    }
  };

  const handleSyncWithTask = async (unitIdOverride) => {
    const syncType = flowApiOptions.bomSyncType;
    if (!syncType) {
      toast.error('Sync with task is not available for this flow.');
      return;
    }

    if (isCommonAreaFlow) {
      if (!projectId) {
        toast.error('Project is required to sync with task.');
        return;
      }

      const syncPayload = buildSyncBomPayload(null, flowApiOptions, {
        projectId,
        combinationName: getBomSyncCombinationName({
          costingData,
          workSections: generatedBomData?.workSections,
          bomSelections
        })
      });

      if (!syncPayload) {
        toast.error('Unable to build sync request.');
        return;
      }

      setIsSyncingWithTask(true);
      try {
        await syncBomWithTask(syncPayload);

        const refreshedBom = await getProjectCommonBom(projectId);
        const activeBoqId =
          generatedBoqData?.boqId ?? generatedBomData?.boqId ?? costingData?.boqId ?? null;
        if (isSavedBomReady(refreshedBom, activeBoqId)) {
          const mapped = mapSavedBomResponse(refreshedBom, costingData, activeBoqId);
          setCostingData(mapped);
          setSavedBomReady(true);
        }
        setBomAlreadyExists(true);
        toast.success('BOM synced with task successfully');
      } catch (err) {
        toast.error(
          err?.response?.data?.message || err?.message || 'Failed to sync BOM with task'
        );
      } finally {
        setIsSyncingWithTask(false);
      }
      return;
    }

    if (!accommodationId) {
      toast.error('Accommodation is required to sync with task.');
      return;
    }

    let syncPayload;
    if (flowApiOptions.syncUsesAccommodationId) {
      syncPayload = buildSyncBomPayload(accommodationId, flowApiOptions);
    } else {
      let unitId = unitIdOverride;
      if (unitId == null && flowApiOptions.bomSyncType === 'GroupUnit') {
        if (!projectId) {
          toast.error('Project is required to sync with task.');
          return;
        }
        try {
          const units = await getUnitsByProjectId(projectId);
          unitId = findUnitIdForAccommodationSync(units, accommodationId);
        } catch (err) {
          toast.error(
            err?.response?.data?.message || err?.message || 'Failed to load units for sync'
          );
          return;
        }
        if (!unitId) {
          toast.error('No unit is linked to this group accommodation.');
          return;
        }
      }
      if (unitId == null) {
        toast.error('Select a unit to sync with task.');
        return;
      }
      syncPayload = buildSyncBomPayload(accommodationId, flowApiOptions, { unitId });
    }

    if (!syncPayload) {
      toast.error('Sync with task is only available for group or independent accommodation.');
      return;
    }

    setIsSyncingWithTask(true);
    try {
      await syncBomWithTask(syncPayload);

      const refreshedBom = await getSavedBom(accommodationId);
      const activeBoqId =
        generatedBoqData?.boqId ?? generatedBomData?.boqId ?? costingData?.boqId ?? null;
      if (isSavedBomReady(refreshedBom, activeBoqId)) {
        const mapped = mapSavedBomResponse(refreshedBom, costingData, activeBoqId);
        setCostingData(mapped);
        setSavedBomReady(true);
      }
      setIsSyncUnitModalOpen(false);
      setBomAlreadyExists(true);
      toast.success('BOM synced with task successfully');
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to sync BOM with task'
      );
    } finally {
      setIsSyncingWithTask(false);
    }
  };

  const handleCheckInventory = async () => {
    if (bomCostingApplied) {
      if (
        !isCommonAreaFlow &&
        getBomSyncUnitType(location.pathname) === 'IndependentUnit'
      ) {
        setIsSyncUnitModalOpen(true);
        return;
      }
      await handleSyncWithTask();
      return;
    }

    if (isCommonAreaFlow) {
      if (!projectId) {
        toast.error('Project is required to apply costing.');
        return;
      }
    } else if (!accommodationId) {
      toast.error('Accommodation is required to apply costing.');
      return;
    }

    const boqId = generatedBomData?.boqId ?? generatedBoqData?.boqId;
    const bomByBoqItem = generatedBomData?.bomByBoqItem;
    if (!boqId || !bomByBoqItem?.length) {
      toast.error('Generate BOM first before checking inventory.');
      return;
    }

    const selections = buildBomCostingSelections(
      generatedBomData.workSections,
      bomSelections
    );
    if (!selections.length) {
      toast.error('Select a combination for each BOQ item.');
      return;
    }

    const costingPayload = {
      boq_id: boqId,
      bomByBoqItem,
      selections,
      quality_level: 'standard'
    };
    if (!isCommonAreaFlow) {
      costingPayload.accommodation_id = accommodationId;
    }

    setIsApplyingCosting(true);
    try {
      const response = await applyBomCosting(
        buildApplyBomCostingPayload(
          costingPayload,
          flowApiOptions,
          isCommonAreaFlow ? { projectId } : {}
        )
      );
      const mapped = mapBomApplyCostingResponse(response);
      setCostingData(mapped);
      setBomCostingApplied(true);

      const savedBomResponse = isCommonAreaFlow
        ? await getProjectCommonBom(projectId)
        : await getSavedBom(accommodationId);
      setSavedBomReady(
        isSavedBomReady(savedBomResponse, boqId) ||
          isSavedBomReady(response) ||
          mapped.savedMaterials.length > 0
      );

      window.scrollTo(0, 0);
      toast.success('Costing applied and BOM saved successfully');
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to apply costing'
      );
    } finally {
      setIsApplyingCosting(false);
    }
  };

  const pageHeaderProps = useMemo(() => {
    const base = {
      breadcrumbLabel: decodedName,
      tabs: DETAIL_TABS,
      activeTab,
      onTabChange: setActiveTab
    };

    if (activeTab === 'BOM' && bomGenerated) {
      return {
        ...base,
        title: bomCostingApplied
          ? `${decodedName} · BOM Costed`
          : `${decodedName} · BOM Generated`,
        subtitle: bomCostingApplied
          ? 'Priced materials from your selected combinations'
          : BOM_GENERATED_SUBTITLE,
        trailing: (
          <AccuracyRateGauge
            value={
              bomCostingApplied
                ? 100
                : generatedBomData?.consistencyScore ?? analysisData?.accuracy ?? 82
            }
          />
        )
      };
    }

    if (activeTab === 'BOQ' && boqGenerated) {
      return {
        ...base,
        title: `${decodedName} · BOQ Generated`,
        subtitle:
          generatedBoqData?.readinessStatus != null
            ? `BOQ readiness: ${generatedBoqData.readinessStatus} · ${generatedBoqData.totalBoqItems ?? 0} items`
            : BOQ_BOM_GENERATED_SUBTITLE,
        trailing:
          generatedBoqData?.overallConfidence != null ? (
            <AccuracyRateGauge value={generatedBoqData.overallConfidence} />
          ) : null
      };
    }

    if (activeTab === 'BOQ' && analysisComplete) {
      return {
        ...base,
        title: `${decodedName} - BOQ Generated`,
        subtitle: BOQ_BOM_GENERATED_SUBTITLE
      };
    }

    return {
      ...base,
      title: decodedName,
      subtitle: BOQ_BOM_APP_TITLE
    };
  }, [
    activeTab,
    analysisComplete,
    bomGenerated,
    boqGenerated,
    decodedName,
    analysisData?.accuracy,
    generatedBoqData,
    bomCostingApplied
  ]);

  return (
    <Layout>
      <BoqBomDetailChrome
        collapsed={collapsed}
        onBack={() => navigate('/builder/operations/boq-bom')}
        projectName={projectName}
        flowLabel={breadcrumbFlowLabel}
        pageHeaderProps={pageHeaderProps}
      />

      <div
        className={`min-h-screen bg-[#F4F6F7] flex flex-col ${BOQ_BOM_DETAIL_CONTENT_OFFSET.withTabs}`}>
        <div
          className={`flex-1 px-6 md:px-8 ${
            (activeTab === 'BOQ' && (analysisComplete || boqGenerated)) ||
            (activeTab === 'BOM' && bomGenerated)
              ? 'pb-36'
              : 'pb-28'
          } ${activeTab === 'BOQ' || (activeTab === 'BOM' && bomGenerated) ? 'pt-6' : 'pt-8'}`}>
          {isRestoringBoq && (
            <div className="flex justify-center py-24">
              <Loader />
            </div>
          )}

          {activeTab === 'BOQ' && !isRestoringBoq && !boqGenerated && !analysisComplete && (
            <div className="mx-auto max-w-[720px]">
              {isAccommodationDrawingFlow ? (
                <div className="overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
                  <div className="border-b border-[#F1F5F9] bg-[#FAFBFC] px-5 py-4">
                    <h2 className="text-[17px] font-semibold text-[#111827]">
                      Drawing sources for {decodedName}
                    </h2>
                    <p className="mt-1 text-[13px] text-[#6B7280]">
                      Choose stored drawings, use all, or add extra PDFs for this analysis.
                    </p>
                  </div>
                  <div className="space-y-5 p-5">
                    <BoqAccommodationDrawingsPicker
                      drawings={accommodationDrawings}
                      isLoading={isLoadingAccommodationDrawings}
                      isError={isAccommodationDrawingsError}
                      useAllDrawings={useAllDrawings}
                      onUseAllDrawingsChange={handleUseAllDrawingsChange}
                      selectedFileKeys={selectedDrawingFileKeys}
                      onToggleFileKey={handleToggleDrawingFileKey}
                      onSelectAll={handleSelectAllDrawings}
                      onClearSelection={handleClearDrawingSelection}
                      disabled={isAnalyzing}
                    />
                    <div className="border-t border-[#F1F5F9] pt-5">
                      <BoqFileUploadSection
                        categoryName={decodedName}
                        files={files}
                        fileInputRef={fileInputRef}
                        isDragging={isDragging}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onBrowseFiles={handleBrowseFiles}
                        onFileInputChange={handleFileInputChange}
                        onRemoveFile={handleRemoveFile}
                        optionalUpload
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <BoqFileUploadSection
                  categoryName={decodedName}
                  files={files}
                  fileInputRef={fileInputRef}
                  isDragging={isDragging}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onBrowseFiles={handleBrowseFiles}
                  onFileInputChange={handleFileInputChange}
                  onRemoveFile={handleRemoveFile}
                />
              )}
            </div>
          )}

          {activeTab === 'BOQ' && !isRestoringBoq && boqGenerated && generatedBoqData && (
            <BoqGeneratedOutput generatedBoqData={generatedBoqData} />
          )}

          {activeTab === 'BOQ' && !isRestoringBoq && analysisComplete && !boqGenerated && (
            <BoqAnalysisResults
              onUploadMore={handleUploadMore}
              onAnswerQuestions={handleAnswerQuestions}
              aiFoundFiles={analysisData?.aiFoundFiles}
              missingData={analysisData?.missingData}
              boqRows={analysisData?.boqRows}
              roomSpaceRows={analysisData?.roomSpaceRows}
              onSaveRoom={handleSaveRoom}
            />
          )}

          {activeTab === 'BOM' && !isRestoringBoq && bomGenerated && bomCostingApplied && costingData && (
            <BoqBomCostingResults
              costingData={costingData}
              onSaveMaterial={handleSaveBomMaterial}
              savingRowId={savingMaterialRowId}
              readOnly={bomAlreadyExists}
              inventoryOptions={inventoryOptions}
              onLinkInventory={handleLinkBomInventory}
              linkingRowId={linkingRowId}
            />
          )}

          {activeTab === 'BOM' && !isRestoringBoq && bomGenerated && !bomCostingApplied && (
            <BoqBomGeneratedResults
              bomData={generatedBomData}
              selectedByBoqItem={bomSelections}
              onSelectionChange={setBomSelections}
            />
          )}

          {activeTab === 'BOM' && !isRestoringBoq && !bomGenerated && (
            <BomTabPanel categoryName={decodedName} />
          )}
        </div>

        {activeTab === 'BOQ' && !isRestoringBoq && !boqGenerated && !analysisComplete && (
          <BoqUploadFooter
            collapsed={collapsed}
            onAnalyzeFiles={handleAnalyzeFiles}
            disabled={!canAnalyzeDrawings}
            isLoading={isAnalyzing}
            loadingLabel={analyzeLoadingLabel}
          />
        )}

        {activeTab === 'BOQ' && boqGenerated && (
          <BoqAnalysisFooter
            collapsed={collapsed}
            boqGenerated
            accuracy={generatedBoqData?.overallConfidence ?? analysisData?.accuracy ?? 0}
            aiValueCount={generatedBoqData?.totalBoqItems ?? 0}
            footerNote={
              bomAlreadyExists
                ? `${generatedBoqData?.totalBoqItems ?? 0} BOQ items generated. BOM is already created.`
                : `${generatedBoqData?.totalBoqItems ?? 0} BOQ items generated. Export BOM for material combinations.`
            }
            onExportBom={handleExportBom}
            isExportingBom={isExportingBom}
            exportBomDisabled={!generatedBoqData?.boqId}
            showExportBom={!bomAlreadyExists}
            showRegenerateBoq={needsBoqRegeneration}
            onRegenerateBoq={handleRegenerateBoq}
            isRegeneratingBoq={isGeneratingBoq}
            onReupload={handleReupload}
            onCreateTasks={
              boqTaskKind && generatedBoqData?.boqId
                ? () => setIsCreateTasksModalOpen(true)
                : undefined
            }
          />
        )}

        {activeTab === 'BOQ' && analysisComplete && !boqGenerated && (
          <BoqAnalysisFooter
            collapsed={collapsed}
            accuracy={analysisData?.accuracy ?? 0}
            aiValueCount={analysisData?.aiValueCount ?? 0}
            footerNote={analysisData?.footerNote}
            roomCoverageScore={analysisData?.summaryIntelligence?.roomCoverageScore}
            worktypeCoverageScore={analysisData?.summaryIntelligence?.worktypeCoverageScore}
            readyForBoqEngine={readyForBoqEngine}
            onAnswerQuestions={() => navigateToQuestions('Clarification questions')}
            onGenerateBoq={handleGenerateBoq}
            isGeneratingBoq={isGeneratingBoq}
            extractRequiredError={extractRequiredError}
            onUploadFiles={handleUploadMore}
          />
        )}

        {activeTab === 'BOM' && bomGenerated && (
          <BoqBomGeneratedFooter
            collapsed={collapsed}
            materialCount={
              bomCostingApplied
                ? costingData?.materialCount ?? 0
                : generatedBomData?.materialCount ?? 0
            }
            aiAssumedCount={generatedBomData?.aiAssumedCount ?? 0}
            onCheckInventory={handleCheckInventory}
            isApplyingCosting={isApplyingCosting}
            checkInventoryLabel={
              bomCostingApplied ? 'Sync with task' : 'Check inventory list'
            }
            isSyncingWithTask={isSyncingWithTask}
          />
        )}

        <CreateTasksFromBoqModal
          isOpen={isCreateTasksModalOpen}
          kind={boqTaskKind}
          projectId={projectId}
          accommodationId={accommodationId}
          accommodationName={decodedName}
          boqId={generatedBoqData?.boqId}
          boqWorkTypeIds={generatedBoqData?.workTypeIds ?? []}
          canAttachBom={bomAlreadyExists}
          buildBomSyncPayload={(unitId) =>
            buildSyncBomPayload(accommodationId, flowApiOptions, { unitId })
          }
          onClose={() => setIsCreateTasksModalOpen(false)}
        />

        <BomSyncUnitModal
          isOpen={isSyncUnitModalOpen}
          projectId={projectId}
          accommodationId={accommodationId}
          accommodationName={decodedName}
          onClose={() => {
            if (!isSyncingWithTask) setIsSyncUnitModalOpen(false);
          }}
          onConfirm={handleSyncWithTask}
          isSyncing={isSyncingWithTask}
        />
      </div>
    </Layout>
  );
};

export default BoqBomDetail;
